import styled from 'vue-styled-components';
import { mapGetters, mapState } from 'vuex';
import { filter } from 'rxjs/operators';

import Logo from './Logo';
import MenuEach from './MenuEach';
import Workspace from './Workspace';

import CloseIcon from '@svgIcons/close2.svg';

import {
    FLEX_CENTER_DIV,
    FLEX_COLUMN_DIV,
    FLEX_VERTICAL_CENTER_DIV,
} from '@styles/common.styled.component';

import { MENU_TYPES, PLAN_A_MENUS, PLAN_T_MENUS } from './const';
import { ACCOUNT_ROLES_NEW } from '@consts';
import { BreakPoint, pickBreakpoint } from '@/utils/layout';
import globalMixin from '@/mixins/global';
import { isValueExist } from '@util';

export const NAVIGATION_HEIGHT = 64;

export default {
    name: 'Navagation',
    mixins: [globalMixin],
    render() {
        const {
            account,
            topMenus,
            bottomMenus,
            globalUser,
            globalAccount,
            workspaceId,
            hubspotBannerHeight,
            expanded,
        } = this;

        if (!globalUser || !globalAccount) {
            return null;
        }

        const path = this.$route.matched[0].path;
        // Segment-aware rather than a substring test: /teamspaces/settings ends
        // with the Settings menu's own path, which lit both menus at once.
        const matcher = (to) =>
            Boolean(to) && (path === to || path.startsWith(`${to}/`));

        return (
            <Wrapper
                activeMobileMenu={expanded}
                hubspotBannerHeight={hubspotBannerHeight ?? 0}
                class="header print-side-nav"
            >
                <Header class="lnb-header">
                    <Logo
                        class="lnb-logo"
                        workspaceId={workspaceId}
                        handleClick={() => {
                            if (!this.isMobileSize) return;

                            this.expanded = !this.expanded;
                        }}
                    />
                    <Title class="lnb-title adriel-ellipsis">
                        {account?.name}
                    </Title>
                    <HeaderRight class="lnb-header-right">
                        {expanded && (
                            <CloseIcon
                                v-view-box
                                class="lnb-header-right-close-icon"
                                onClick={() => {
                                    this.expanded = false;
                                }}
                            />
                        )}
                    </HeaderRight>
                </Header>
                <TopMenus class="lnb-top-menus">
                    {topMenus.map(
                        ({
                            type,
                            openNew,
                            to,
                            icon,
                            activeIcon,
                            beta,
                            nested = [],
                        }) => {
                            const active = R.any(matcher, [to, ...nested]);
                            const onExactRoute = matcher(to);

                            if (openNew) {
                                return (
                                    <a href={to} target="_blank">
                                        <MenuEach
                                            type={type}
                                            active={active}
                                            icon={icon}
                                            activeIcon={activeIcon}
                                            beta={beta}
                                        />
                                    </a>
                                );
                            }

                            return (
                                <router-link
                                    to={to}
                                    event={onExactRoute ? '' : 'click'}
                                    nativeOnClick={() =>
                                        (this.expanded = false)
                                    }
                                >
                                    <MenuEach
                                        type={type}
                                        active={active}
                                        icon={icon}
                                        activeIcon={activeIcon}
                                        beta={beta}
                                    />
                                </router-link>
                            );
                        }
                    )}
                </TopMenus>
                <BottomMenus class="lnb-bottom-menus">
                    {bottomMenus.map(
                        ({
                            type,
                            openNew,
                            to,
                            icon,
                            activeIcon,
                            beta,
                            nested = [],
                        }) => {
                            const active = R.any(matcher, [to, ...nested]);
                            const onExactRoute = matcher(to);

                            if (openNew) {
                                return (
                                    <a href={to} target="_blank">
                                        <MenuEach
                                            type={type}
                                            active={active}
                                            icon={icon}
                                            activeIcon={activeIcon}
                                            beta={beta}
                                        />
                                    </a>
                                );
                            }

                            return (
                                <router-link
                                    to={to}
                                    event={onExactRoute ? '' : 'click'}
                                    nativeOnClick={() =>
                                        (this.expanded = false)
                                    }
                                >
                                    <MenuEach
                                        type={type}
                                        active={active}
                                        icon={icon}
                                        activeIcon={activeIcon}
                                        beta={beta}
                                    />
                                </router-link>
                            );
                        }
                    )}
                    <Workspace workspaceId={workspaceId} />
                </BottomMenus>
            </Wrapper>
        );
    },
    computed: {
        ...mapGetters('permission', [
            'connectionNavVisible',
            'campaignCreatable',
        ]),
        ...mapGetters('user', [
            'accountId',
            'isKoUser',
            'pricePlanSettings',
            'account',
            'role',
        ]),
        ...mapState('user', ['dashboardClient', 'dashboardClientCreated$']),
        ...mapState('newDashboard', ['hubspotBannerHeight']),
        showCampaign() {
            return this.account?.account_type === 'A' && this.campaignCreatable;
        },
        showDataStream() {
            const { account, pricePlanSettings } = this;
            const allowDataTransfer = R.propOr(
                false,
                'allowDataTransfer'
            )(pricePlanSettings);

            return account?.meta?.allowDatastream || allowDataTransfer;
        },
        showDashboard() {
            return !this.account?.meta?.hideDashboard;
        },
        showConnection() {
            return this.connectionNavVisible;
        },
        showGuide() {
            return !this.isWhitelabelUrl;
        },
        showAiAgent() {
            const { pricePlanSettings } = this;
            const workspaceFlag = R.prop('allowAIAgent')(pricePlanSettings);
            const userFlag = this.globalUser?.meta?.allowAIFeature;
            return workspaceFlag !== false && userFlag !== false;
        },
        showCustomerJourney() {
            return this.hasJourneyAdAccounts;
        },
        showSpendRules() {
            // ADMIN and VIEW_AND_EDIT may open the Margin Management (spend
            // rules) page; check the whole role array so a VIEW_AND_EDIT +
            // CONNECTION_MANAGEMENT combo also passes.
            return (
                Boolean(this.account?.meta?.spendRule) &&
                (this.role?.includes(ACCOUNT_ROLES_NEW.ADMIN) ||
                    this.role?.includes(ACCOUNT_ROLES_NEW.VIEW_AND_EDIT))
            );
        },
        showTeamspace() {
            return (
                this.role?.includes(ACCOUNT_ROLES_NEW.ADMIN) ||
                this.role?.includes(ACCOUNT_ROLES_NEW.VIEW_AND_EDIT)
            );
        },
        menus() {
            const MENUS = (this.isPlanT ? PLAN_T_MENUS : PLAN_A_MENUS)
                .map(
                    R.when(R.propEq('type', MENU_TYPES.guide), (obj) => {
                        return {
                            ...obj,
                            to: 'https://docs.adriel.com/',
                        };
                    })
                )
                .filter(({ type }) => {
                    switch (type) {
                        case MENU_TYPES.ai_agent:
                            return this.showAiAgent;
                        case MENU_TYPES.campaign:
                            return this.showCampaign;
                        case MENU_TYPES.connection:
                            return this.showConnection;
                        case MENU_TYPES.data_stream:
                            return this.showDataStream;
                        case MENU_TYPES.dashboard:
                            return this.showDashboard;
                        case MENU_TYPES.guide:
                            return this.showGuide;
                        case MENU_TYPES.customer_journey:
                            return this.showCustomerJourney;
                        case MENU_TYPES.spend_rules:
                            return this.showSpendRules;
                        case MENU_TYPES.teamspace:
                            return this.showTeamspace;
                        default:
                            return true;
                    }
                });

            return MENUS;
        },
        topMenus() {
            return this.menus.filter(({ position }) => position === 'top');
        },
        bottomMenus() {
            return this.menus.filter(({ position }) => position === 'bottom');
        },
    },
    // TODO delete mmo
    watch: {
        accountId: {
            handler(accountId) {
                this.workspaceId = accountId;
            },
            immediate: true,
        },
    },
    // TODO delete mmo
    data() {
        return {
            expanded: false,
            workspaceId: undefined,
            hasJourneyAdAccounts: false,
        };
    },
    mounted() {
        if (isValueExist(this.dashboardClient)) {
            this.dashboardClientCreated$.next(true);
        }

        this.createClient$();
        this.fetchJourneyAdAccounts$();
    },
    methods: {
        fetchJourneyAdAccounts$() {
            this.$subscribeTo(
                this.dashboardClientCreated$.pipe(filter(R.identity)),
                async () => {
                    try {
                        if (!this.dashboardClient) {
                            this.hasJourneyAdAccounts = false;
                            return;
                        }

                        const journeyAdAccounts =
                            await this.dashboardClient.getAdAccounts({
                                customerJourneyOnly: true,
                            });

                        this.hasJourneyAdAccounts =
                            isValueExist(journeyAdAccounts);
                    } catch (err) {
                        console.error('fetchJourneyAdAccounts error:', err);
                        this.hasJourneyAdAccounts = false;
                    }
                }
            );
        },
    },
};

const Wrapper = styled(FLEX_COLUMN_DIV, { activeMobileMenu: Boolean })`
    width: 72px;
    height: 100vh;
    overflow: hidden;
    padding: 12px 0 16px;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;

    position: sticky;
    top: 0;
    left: 0;
    z-index: 12;

    background-color: white;
    border-right: 1px solid var(--color-gray-30);

    .lnb-title,
    .lnb-header-right {
        display: none;
    }

    .lnb-header-right-close-icon {
        width: 24px;
        height: 24px;
        cursor: pointer;
    }

    @media print {
        display: none !important;
    }

    @media screen and (max-width: ${pickBreakpoint(BreakPoint.lg) - 1}px) {
        width: 48px;
    }

    @media screen and (max-width: ${pickBreakpoint(BreakPoint.md) - 1}px) {
        position: sticky;
        width: 100%;
        height: ${({ activeMobileMenu }) =>
            activeMobileMenu ? '100vh' : `${NAVIGATION_HEIGHT}px`};
        padding: 0 16px;
        gap: 20px;

        & > div {
            width: 100%;
        }

        .lnb-top-menus {
            overflow-y: auto;
        }

        .lnb-bottom-menus {
            padding: 20px 0 40px;
            border-top: 1px solid var(--color-gray-30);
        }

        .lnb-header {
            height: ${NAVIGATION_HEIGHT}px;

            .lnb-logo {
                cursor: pointer;
            }

            .lnb-title {
                display: block;
            }

            .lnb-header-right {
                display: flex;
            }
        }
    }
`;

const Header = styled(FLEX_CENTER_DIV)`
    gap: 10px;
    flex-shrink: 0;

    .lnb-logo,
    .lnb-header-right {
        flex-shrink: 0;
    }
`;

const Title = styled.div`
    margin: 0 auto;

    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: -0.1px;
    color: var(--color-gray-100);
    text-align: center;
`;

const HeaderRight = styled(FLEX_VERTICAL_CENTER_DIV)`
    width: 40px;
    height: 40px;
    justify-content: end;
`;

const TopMenus = styled(FLEX_COLUMN_DIV)`
    gap: 4px;
`;

const BottomMenus = styled(FLEX_COLUMN_DIV)`
    margin-top: auto;
    gap: 4px;
`;
